import { ArrowSquareOut, CheckCircle, PencilSimple, Trash, WarningCircle, XCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SetThumb from "@/components/opportunities/SetThumb";
import { ApproveCell, SmartOfferCell, SuggestedOfferCell } from "@/components/deals/OfferCells";
import { amazonUrl, bricklinkUrl } from "@/lib/links";
import { money, num } from "@/components/deals/format";

export function DealRow({ r, tab, onDelete, checked, onToggle, onDataChanged, isVisible, onOpenManualPrice, onEditOffer }) {
  const amz = r.last_scan_amazon || {};
  const bl = r.last_scan_bricklink || {};
  const pc = r.last_scan_pricecharting || {};
  const cost = r.buy_cost;
  const qty = r.qty || 1;
  const bb = amz.buy_box ?? r.current_buy_box;
  const pl = (cost != null && bb != null) ? bb - cost : null;
  const notListed = amz.amazon_listed === false;
  const latestScanAt = [amz.scanned_at, bl.scanned_at, pc.scanned_at].filter(Boolean).sort().reverse()[0];

  const resolvedAsin = amz.asin || r.asin || null;
  const amzHref = amazonUrl(resolvedAsin, r.set_number, !!resolvedAsin);
  const blHref = bricklinkUrl(r.set_number);

  // Copy ASIN to clipboard on click. The link itself still opens Amazon
  // in a new tab (default anchor behavior with target="_blank"), so the
  // user gets both: value copied + market page opened.
  const copyAsin = (e) => {
    if (!resolvedAsin) return;
    try {
      navigator.clipboard.writeText(resolvedAsin);
      toast.success(`Copied ${resolvedAsin}`);
    } catch { /* ignore — link click still opens Amazon */ }
  };

  const now = Date.now();
  const isFresh = (iso) => {
    if (!iso) return false;
    const t = Date.parse(iso);
    return Number.isFinite(t) && (now - t) < 24 * 3600 * 1000;
  };
  const amzFresh = isFresh(amz.scanned_at);
  const blFresh = isFresh(bl.scanned_at);
  const pcFresh = isFresh(pc.scanned_at);

  return (
    <tr className="border-b border-[#F6F6F3] hover:bg-[#F6F6F3]" data-testid={`deals-row-${r.id}`}>
      <td className="px-2 py-2 text-center align-middle">
        {r.set_number && (
          <input
            type="checkbox"
            checked={!!checked}
            onChange={onToggle}
            className="accent-[#E3000B] w-3 h-3 cursor-pointer"
            aria-label={`Select ${r.set_number}`}
            data-testid={`deals-select-${r.set_number}`}
          />
        )}
      </td>
      <td className="px-3 py-2">
        {r.set_number ? (
          <Link to={`/set/${r.set_number}`} className="text-[#E3000B] hover:underline">{r.set_number}</Link>
        ) : <span className="text-gray-400">—</span>}
      </td>
      {isVisible("name") && (
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <SetThumb src={r.image_url} alt={r.name} size={26} />
          <span className="text-gray-900 truncate max-w-[220px]" title={r.title || r.name}>{r.name || r.title || "—"}</span>
        </div>
      </td>
      )}
      {isVisible("asin") && (
      <td className="px-3 py-2">
        {resolvedAsin ? (
          <a
            href={amazonUrl(resolvedAsin, r.set_number, true)}
            target="_blank" rel="noopener noreferrer"
            onClick={copyAsin}
            data-testid={`asin-link-${r.set_number}`}
            title={`Click: copy ASIN + open Amazon${amz.asin ? " (Keepa-verified)" : " (from upload)"}`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm font-mono text-[10px] font-bold hover:underline ${
              amz.asin
                ? "border-[#00A850]/40 bg-[#00A850]/[0.08] text-[#00A850]"
                : "border-[#D97706]/40 bg-[#D97706]/[0.08] text-[#D97706]"
            }`}
          >{resolvedAsin}</a>
        ) : notListed ? (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#71717A]/40 bg-[#71717A]/[0.10] text-gray-600 rounded-sm font-mono text-[10px]"
            title={`Confirmed not on Amazon${amz.scanned_at ? ` · scanned ${new Date(amz.scanned_at).toLocaleString()}` : ""}`}
            data-testid={`asin-not-on-amazon-${r.set_number}`}
          >
            <XCircle size={10} weight="fill" /> NOT ON AMAZON
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#FF3333]/40 bg-[#FF3333]/[0.06] text-[#FF3333] rounded-sm font-mono text-[10px]" title="No ASIN saved for this set — scan Amazon to find one">
            <WarningCircle size={10} weight="fill" /> NO ASIN
          </span>
        )}
      </td>
      )}
      {isVisible("qty") && <td className="px-3 py-2 text-right text-gray-800">{qty}</td>}
      {isVisible("paid") && <td className="px-3 py-2 text-right text-gray-800">{money(cost)}</td>}
      {isVisible("amz_bb") && (
      <td className="px-3 py-2 text-right">
        {notListed ? (
          <span className="inline-flex items-center gap-1 text-[#FF3333]" title="Not on Amazon"><WarningCircle size={11} weight="fill" /> N/A</span>
        ) : bb != null ? (
          <span className="inline-flex items-center gap-1.5 justify-end">
            {amz.buy_box_manual && (
              <span title="Manual override" className="inline-flex items-center px-1 py-0.5 border border-[#E3000B]/60 rounded-sm text-[9px] font-bold text-[#E3000B] bg-[#E3000B]/[0.08]">M</span>
            )}
            {!amz.buy_box_manual && <FreshDot fresh={amzFresh} label={amzFresh
              ? `Live · fetched ${new Date(amz.scanned_at).toLocaleString()}`
              : (amz.scanned_at ? `Stale · fetched ${new Date(amz.scanned_at).toLocaleString()}` : "Not scanned yet — value is mock/enriched")
            } testId={`amz-status-${r.set_number}`} />}
            <a href={amzHref} target="_blank" rel="noopener noreferrer" title={resolvedAsin ? `Open ${resolvedAsin} on Amazon` : "Search on Amazon"}
               className="text-[#E3000B] hover:underline hover:text-[#D97706]"
               data-testid={`amz-price-link-${r.set_number}`}>{money(bb)}</a>
            <button
              onClick={() => onOpenManualPrice("amz_buy_box", r.set_number, bb, !!amz.buy_box_manual, null)}
              title="Edit — pin the true Buy Box"
              data-testid={`amz-edit-${r.set_number}`}
              className="text-gray-500 hover:text-[#E3000B]"
            ><PencilSimple size={11} weight="bold" /></button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 justify-end">
            <FreshDot fresh={false} label="Never scanned" testId={`amz-status-${r.set_number}`} />
            <span className="text-gray-400">--</span>
            <button
              onClick={() => onOpenManualPrice("amz_buy_box", r.set_number, null, false, null)}
              title="Set Buy Box manually"
              data-testid={`amz-edit-${r.set_number}`}
              className="text-gray-500 hover:text-[#E3000B]"
            ><PencilSimple size={11} weight="bold" /></button>
          </span>
        )}
      </td>
      )}
      {isVisible("fba_fbm") && (
      <td className="px-3 py-2 text-right text-gray-800">
        {amz.fba_buy_box != null && (
          <a href={amzHref} target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline" title={resolvedAsin ? `FBA · Open ${resolvedAsin} on Amazon` : "FBA · Search on Amazon"}>{money(amz.fba_buy_box)}</a>
        )}
        {amz.fba_buy_box != null && amz.fbm_buy_box != null && <span className="text-gray-400 mx-1">/</span>}
        {amz.fbm_buy_box != null && (
          <a href={amzHref} target="_blank" rel="noopener noreferrer" className="text-[#E3000B] hover:underline" title={resolvedAsin ? `FBM · Open ${resolvedAsin} on Amazon` : "FBM · Search on Amazon"}>{money(amz.fbm_buy_box)}</a>
        )}
        {amz.fba_buy_box == null && amz.fbm_buy_box == null && <span className="text-gray-400">--</span>}
      </td>
      )}
      {isVisible("stock") && (
      <td className="px-3 py-2 text-center">
        {amz.scanned_at ? (
          amz.in_stock
            ? <CheckCircle size={14} weight="fill" className="text-[#00A850] inline" />
            : <XCircle size={14} weight="fill" className="text-[#FF3333] inline" />
        ) : <span className="text-gray-400">--</span>}
      </td>
      )}
      {isVisible("rank") && <td className="px-3 py-2 text-right text-gray-800">{num(amz.sales_rank)}</td>}
      {isVisible("bl_lowest") && (
      <td className="px-3 py-2 text-right">
        {bl.lowest_sealed_us != null ? (
          <span className="inline-flex items-center gap-1.5 justify-end">
            {bl.manual ? (
              <span
                title={r.manual_bl_note ? `Manual · ${r.manual_bl_note}` : "Manually set — overrides scan"}
                className="inline-flex items-center px-1 py-0.5 border border-[#E3000B]/60 rounded-sm text-[9px] font-bold text-[#E3000B] bg-[#E3000B]/[0.08]"
                data-testid={`bl-manual-badge-${r.set_number}`}
              >M</span>
            ) : blFresh && (
              <LiveDot label={`Live · fetched ${new Date(bl.scanned_at).toLocaleString()}`} testId={`bl-fresh-${r.set_number}`} />
            )}
            <a href={blHref} target="_blank" rel="noopener noreferrer"
               title={bl.manual ? "Manual override — click to verify on BrickLink" : `Verify on BrickLink · ${bl.seller_count || 0} US sellers · ${bl.qty_available || 0} qty`}
               className={bl.manual ? "text-[#E3000B] hover:underline" : "text-[#0055BF] hover:underline"}>
              {money(bl.lowest_sealed_us)}
            </a>
            <button
              onClick={() => onOpenManualPrice("bl_sealed", r.set_number, bl.lowest_sealed_us, !!bl.manual, r.manual_bl_note)}
              title="Edit — pin the true US sealed price"
              data-testid={`bl-edit-${r.set_number}`}
              className="text-gray-500 hover:text-[#E3000B]"
            ><PencilSimple size={11} weight="bold" /></button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 justify-end">
            <span className="text-gray-400">--</span>
            <button
              onClick={() => onOpenManualPrice("bl_sealed", r.set_number, null, false, null)}
              title="Set the true US sealed price manually"
              data-testid={`bl-edit-${r.set_number}`}
              className="text-gray-500 hover:text-[#E3000B]"
            ><PencilSimple size={11} weight="bold" /></button>
          </span>
        )}
      </td>
      )}
      {isVisible("bl_avg") && (
      <td className="px-3 py-2 text-right">
        <span className="inline-flex items-center gap-1.5 justify-end">
          {bl.avg_manual && (
            <span title="Manual override" className="inline-flex items-center px-1 py-0.5 border border-[#E3000B]/60 rounded-sm text-[9px] font-bold text-[#E3000B] bg-[#E3000B]/[0.08]">M</span>
          )}
          {bl.avg_sealed != null ? (
            <a href={blHref} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-[#0055BF] hover:underline" title="BL avg — click to verify">
              {money(bl.avg_sealed)}
            </a>
          ) : <span className="text-gray-400">--</span>}
          <button
            onClick={() => onOpenManualPrice("bl_avg", r.set_number, bl.avg_sealed, !!bl.avg_manual, null)}
            title="Edit — pin the true BL avg"
            data-testid={`bl-avg-edit-${r.set_number}`}
            className="text-gray-500 hover:text-[#E3000B]"
          ><PencilSimple size={11} weight="bold" /></button>
        </span>
      </td>
      )}
      {isVisible("pc_sealed") && (
      <td className="px-3 py-2 text-right">
        <span className="inline-flex items-center gap-1.5 justify-end">
          {pc.manual && (
            <span title="Manual override" className="inline-flex items-center px-1 py-0.5 border border-[#E3000B]/60 rounded-sm text-[9px] font-bold text-[#E3000B] bg-[#E3000B]/[0.08]">M</span>
          )}
          {pc.url && pc.sealed_price != null && !pc.manual && pcFresh && (
            <LiveDot label={`Live · fetched ${new Date(pc.scanned_at).toLocaleString()}`} testId={`pc-fresh-${r.set_number}`} />
          )}
          {pc.sealed_price != null ? (
            pc.url ? (
              <a href={pc.url} target="_blank" rel="noopener noreferrer" className="text-[#7C4DFF] hover:underline" title={pc.product_name || "Verify on PriceCharting"}>
                {money(pc.sealed_price)}
              </a>
            ) : <span className="text-[#7C4DFF]">{money(pc.sealed_price)}</span>
          ) : <span className="text-gray-400">--</span>}
          <button
            onClick={() => onOpenManualPrice("pc_sealed", r.set_number, pc.sealed_price, !!pc.manual, null)}
            title="Edit — pin the true PriceCharting price"
            data-testid={`pc-edit-${r.set_number}`}
            className="text-gray-500 hover:text-[#E3000B]"
          ><PencilSimple size={11} weight="bold" /></button>
        </span>
      </td>
      )}
      {isVisible("suggested") && (
        <SuggestedOfferCell offer={r.suggested_offer} setNumber={r.set_number}
          manualOffer={r.offer_is_manual ? r.effective_offer : null}
          onEdit={() => onEditOffer?.({ open: true, dealId: r.id, setNumber: r.set_number, auto: r.offer_is_manual ? null : r.effective_offer, current: r.effective_offer, isManual: !!r.offer_is_manual })} />
      )}
      {isVisible("smart_offer") && <SmartOfferCell so={r.smart_offer} setNumber={r.set_number} />}
      {isVisible("unit_pl") && (
      <td className={`px-3 py-2 text-right font-bold ${pl == null ? "text-gray-400" : pl >= 0 ? "text-[#00A850]" : "text-[#FF3333]"}`}>
        {pl == null ? "--" : `${pl >= 0 ? "+" : "-"}$${Math.abs(pl).toFixed(2)}`}
      </td>
      )}
      {isVisible("approve") && <ApproveCell r={r} onDataChanged={onDataChanged} />}
      {isVisible("last_scan") && (
      <td className="px-3 py-2 text-center text-[10px] text-gray-500">
        {latestScanAt ? new Date(latestScanAt).toLocaleDateString() : <span className="text-gray-400">never</span>}
      </td>
      )}
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {resolvedAsin && (
            <a href={amazonUrl(resolvedAsin, r.set_number, true)} target="_blank" rel="noopener noreferrer" title={`Open ${resolvedAsin} on Amazon`}
               className="inline-flex items-center gap-0.5 px-1.5 h-5 border border-[#D97706]/60 rounded-sm text-[9px] font-bold text-[#D97706] hover:bg-[#D97706]/10">
              AMZ <ArrowSquareOut size={9} />
            </a>
          )}
          <button onClick={() => onDelete(r.id)} data-testid={`deals-delete-${r.id}`}
            className="text-gray-500 hover:text-[#FF3333]"><Trash size={11} /></button>
        </div>
      </td>
    </tr>
  );
}



export function LiveDot({ label, testId }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-[#00A850] shadow-[0_0_6px_#00A850] animate-pulse"
      title={label || "Live within 24h"}
      data-testid={testId}
      aria-label="live"
    />
  );
}

// Amazon indicator — dual-state so the user can always see the freshness
// state, not just when it's fresh. Green = scanned in the last 24h;
// red = never scanned OR scan is > 24h old.
export function FreshDot({ fresh, label, testId }) {
  const cls = fresh
    ? "bg-[#00A850] shadow-[0_0_6px_#00A850] animate-pulse"
    : "bg-[#FF3333] shadow-[0_0_4px_#FF3333]";
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full ${cls}`}
      title={label}
      data-testid={testId}
      data-fresh={fresh ? "true" : "false"}
      aria-label={fresh ? "live" : "stale"}
    />
  );
}

